import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../template/_common/header";
import SidebarComponent from "../template/admin/sidebarComponent";
import SidebarArrow from "../template/_common/sidebarArrow";
import { globalAlert, globalLoader } from "../actions/commonActions";
import ConfirmationModal from "../template/_common/confirmModal";
import {
  lmsGetLearnerApi,
  lmsUpdateLearnerStatusApi,
  lmsSendLearnerPasswordResetApi,
} from "./_lmsActions";

const idOf = (value) => String(value?._id || value?.id || value || "");

const learnerName = (learner) =>
  learner?.name ||
  `${learner?.firstName || ""} ${learner?.lastName || ""}`.trim() ||
  "-";

const humanize = (value) =>
  value
    ? String(value)
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : "-";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatScore = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (Number.isNaN(number)) return "-";
  return `${Math.round(number)}%`;
};

const initials = (learner) =>
  learnerName(learner)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const statusClass = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (["completed", "complete", "pass", "passed", "active"].includes(normalized))
    return "success";
  if (["failed", "rejected", "expired"].includes(normalized)) return "danger";
  if (["in_progress", "in progress", "pending", "pending-approval"].includes(normalized))
    return "warning";
  return "muted";
};

const Badge = ({ children, tone }) => (
  <span className={`lms-history-badge ${tone || statusClass(children)}`}>{children || "-"}</span>
);

// Account lifecycle → label + tone, mirroring the learners LIST page. The detail
// endpoint only returns the raw `status` ("pending" for not-yet-approved), so
// pending/invited both read as "Not Active" here (invitations aren't used).
const accountStatusOf = (learner) => {
  const raw = String(learner?.lifecycleStatus || learner?.status || "active")
    .toLowerCase()
    .replace(/_/g, "-");
  if (["pending", "pending-approval", "awaiting-approval", "invited", "invitation-sent", "pending-invite"].includes(raw))
    return { key: "not-approved", label: "Not Active", tone: "warning" };
  if (["inactive", "deactivated", "suspended"].includes(raw))
    return { key: "deactivated", label: "Deactivated", tone: "muted" };
  if (raw === "rejected") return { key: "rejected", label: "Rejected", tone: "danger" };
  return { key: "active", label: "Active", tone: "success" };
};

const bestAttemptScore = (attempts) => {
  const scores = attempts
    .map((attempt) => Number(attempt?.scorePercent))
    .filter((score) => !Number.isNaN(score));
  return scores.length ? Math.max(...scores) : null;
};

const assetTitle = (asset, fallback) =>
  asset?.title || asset?.filename || asset?.asset?.title || asset?.asset?.filename || fallback;

const buildAssetMap = (modules) => {
  const map = {};
  modules.forEach((module) => {
    (module?.introductionAssets || []).forEach((asset, index) => {
      map[idOf(asset)] = assetTitle(asset, `Introduction ${index + 1}`);
    });
    (module?.sections || []).forEach((section) => {
      (section?.assets || []).forEach((asset, index) => {
        map[idOf(asset)] = assetTitle(asset, `${section?.title || "Part"} asset ${index + 1}`);
      });
    });
    (module?.completionAssets || []).forEach((asset, index) => {
      map[idOf(asset)] = assetTitle(asset, `Completion PDF ${index + 1}`);
    });
    (module?.supportServicesAssets || []).forEach((entry, index) => {
      const asset = entry?.asset || entry;
      map[idOf(asset)] = assetTitle(asset, `Support services PDF ${index + 1}`);
    });
  });
  return map;
};

const makeHistoryGroups = ({ enrolments, examResults, quizAttempts, moduleCertificates, modules }) => {
  const modulesByCourse = modules.reduce((acc, module) => {
    const courseId = idOf(module?.course);
    acc[courseId] = acc[courseId] || [];
    acc[courseId].push(module);
    return acc;
  }, {});
  const assetMap = buildAssetMap(modules);

  return enrolments.map((enrolment) => {
    const course = enrolment?.course || {};
    const courseId = idOf(course);
    const enrolmentId = idOf(enrolment);
    const courseAttempts = quizAttempts.filter((attempt) => idOf(attempt?.enrolment) === enrolmentId);
    const courseExam = examResults.find(
      (result) => idOf(result?.enrolment) === enrolmentId || idOf(result?.course) === courseId
    );
    const courseRow = {
      key: `course-${enrolmentId}`,
      level: 0,
      label: course?.title || enrolment?.courseTitle || "Course",
      status: humanize(enrolment?.status),
      completedOn: enrolment?.completedAt,
      attempts: courseAttempts.filter((attempt) => attempt?.quizScope === "course").length || "-",
      bestScore: courseExam?.overallScore ?? bestAttemptScore(courseAttempts.filter((attempt) => attempt?.quizScope === "course")),
      pass: courseExam?.passed,
      certificate:
        courseExam?.certificateIssued || courseExam?.certificateNumber
          ? "Issued"
          : course?.hasCertificate
          ? "Pending"
          : "-",
      rowType: "course",
    };

    const courseModules = modulesByCourse[courseId] || [];
    const moduleRows = (enrolment?.moduleProgress || []).flatMap((moduleProgress) => {
      const moduleId = idOf(moduleProgress?.module);
      const module = courseModules.find((item) => idOf(item) === moduleId) || {};
      const moduleAttempts = courseAttempts.filter((attempt) => idOf(attempt?.module) === moduleId);
      const moduleCertificate = moduleCertificates.find(
        (certificate) =>
          idOf(certificate?.enrolment) === enrolmentId && idOf(certificate?.module) === moduleId
      );
      const rows = [
        {
          key: `module-${enrolmentId}-${moduleId}`,
          level: 1,
          label: module?.title || "Module",
          status: humanize(moduleProgress?.status),
          completedOn: moduleProgress?.completedAt,
          attempts: moduleAttempts.length || moduleProgress?.quizAttempts || "-",
          bestScore: moduleProgress?.bestScore ?? bestAttemptScore(moduleAttempts),
          pass: moduleProgress?.hasPassed,
          certificate:
            moduleCertificate?.certificateIssued || moduleCertificate?.certificateNumber
              ? "Issued"
              : module?.hasCertificate
              ? "Pending"
              : "-",
          rowType: "module",
        },
      ];

      (moduleProgress?.moduleIntroProgress || []).forEach((progress, index) => {
        rows.push({
          key: `module-intro-${enrolmentId}-${moduleId}-${idOf(progress?.asset) || index}`,
          level: 2,
          label: assetMap[idOf(progress?.asset)] || `Module introduction ${index + 1}`,
          status: humanize(progress?.status),
          completedOn: progress?.completedAt,
          attempts: "-",
          bestScore: null,
          pass: progress?.status === "completed",
          certificate: "-",
        });
      });

      (moduleProgress?.sectionProgress || []).forEach((sectionProgress, sectionIndex) => {
        const sectionId = idOf(sectionProgress?.section);
        const section = (module?.sections || []).find((item) => idOf(item) === sectionId);
        const sectionAttempts = moduleAttempts.filter((attempt) => idOf(attempt?.sectionId) === sectionId);
        (sectionProgress?.assetProgress || []).forEach((progress, assetIndex) => {
          rows.push({
            key: `section-asset-${enrolmentId}-${moduleId}-${sectionId}-${idOf(progress?.asset) || assetIndex}`,
            level: 2,
            label:
              assetMap[idOf(progress?.asset)] ||
              `${section?.title || `Part ${sectionIndex + 1}`} asset ${assetIndex + 1}`,
            status: humanize(progress?.status),
            completedOn: progress?.completedAt,
            attempts: "-",
            bestScore: null,
            pass: progress?.status === "completed",
            certificate: "-",
          });
        });
        if (sectionProgress?.quiz || sectionAttempts.length || sectionProgress?.quizAttempts) {
          rows.push({
            key: `section-quiz-${enrolmentId}-${moduleId}-${sectionId}`,
            level: 2,
            label: `${section?.title || `Part ${sectionIndex + 1}`} Quiz`,
            status: sectionProgress?.quizPassed ? "Pass" : humanize(sectionProgress?.status),
            completedOn: sectionProgress?.completedAt,
            attempts: sectionAttempts.length || sectionProgress?.quizAttempts || 0,
            bestScore: sectionProgress?.bestScore ?? bestAttemptScore(sectionAttempts),
            pass: sectionProgress?.quizPassed,
            certificate: "-",
          });
        }
      });

      (moduleProgress?.completionAssetProgress || []).forEach((progress, index) => {
        rows.push({
          key: `completion-${enrolmentId}-${moduleId}-${idOf(progress?.asset) || index}`,
          level: 2,
          label: assetMap[idOf(progress?.asset)] || `Completion PDF ${index + 1}`,
          status: humanize(progress?.status),
          completedOn: progress?.completedAt,
          attempts: "-",
          bestScore: null,
          pass: progress?.status === "completed",
          certificate: "-",
        });
      });

      (moduleProgress?.supportServiceAssetProgress || []).forEach((progress, index) => {
        rows.push({
          key: `support-${enrolmentId}-${moduleId}-${idOf(progress?.asset) || index}`,
          level: 2,
          label: assetMap[idOf(progress?.asset)] || `Support services PDF ${index + 1}`,
          status: humanize(progress?.status),
          completedOn: progress?.completedAt,
          attempts: "-",
          bestScore: null,
          pass: progress?.status === "completed",
          certificate: "-",
        });
      });

      return rows;
    });

    return {
      key: courseRow.key,
      course: courseRow,
      children: moduleRows,
    };
  });
};

const LmsLearnerDetail = () => {
  const { learnerId } = useParams();
  const navigate = useNavigate();
  const [navbarToggle, setNavbarToggle] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [openCourseKey, setOpenCourseKey] = useState("");
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [approveSuccess, setApproveSuccess] = useState(null); // { name, message }

  const loadLearner = async ({ silent } = {}) => {
    try {
      if (!silent) setLoading(true);
      const res = await lmsGetLearnerApi(learnerId);
      if (res?.data?.status === 200) {
        setDetail(res?.data?.data || {});
      } else if (!silent) {
        globalAlert("error", res?.data?.message || "Unable to load learner profile.");
        navigate("/admin/lms/learners?status=all");
      }
    } catch (error) {
      globalAlert("error", error?.response?.data?.message || "Unable to load learner profile.");
      if (error?.response?.data?.status === 401) navigate("/admin/login");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadLearner(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learnerId, navigate]);

  const payload = useMemo(
    () => ({
      learner: detail?.learner || {},
      enrolments: Array.isArray(detail?.enrolments) ? detail.enrolments : [],
      examResults: Array.isArray(detail?.examResults) ? detail.examResults : [],
      quizAttempts: Array.isArray(detail?.quizAttempts) ? detail.quizAttempts : [],
      moduleCertificates: Array.isArray(detail?.moduleCertificates) ? detail.moduleCertificates : [],
      modules: Array.isArray(detail?.modules) ? detail.modules : [],
    }),
    [detail]
  );
  const { learner, enrolments, examResults, quizAttempts, moduleCertificates, modules } = payload;

  const historyGroups = useMemo(
    () => makeHistoryGroups({ enrolments, examResults, quizAttempts, moduleCertificates, modules }),
    [enrolments, examResults, quizAttempts, moduleCertificates, modules]
  );
  const historyRowCount = historyGroups.reduce(
    (total, group) => total + 1 + (group?.children?.length || 0),
    0
  );

  useEffect(() => {
    if (!historyGroups.length) {
      setOpenCourseKey("");
      return;
    }
    setOpenCourseKey((current) =>
      historyGroups.some((group) => group.key === current) ? current : historyGroups[0].key
    );
  }, [historyGroups]);

  const completedCourses = enrolments.filter((enrolment) => enrolment?.status === "completed").length;
  const account = accountStatusOf(learner);
  const needsApproval = account.key === "not-approved";

  const approveLearner = async (confirmed) => {
    setConfirmApprove(false);
    if (!confirmed) return;
    try {
      globalLoader(true);
      const res = await lmsUpdateLearnerStatusApi(learnerId, "active");
      if (res?.data?.status === 200) {
        // Approval also emails the learner a link to set their password.
        let resetSent = false;
        try {
          const pr = await lmsSendLearnerPasswordResetApi(learnerId);
          resetSent = pr?.data?.status === 200;
        } catch (e) {
          /* non-fatal — the learner is approved either way */
        }
        globalLoader(false);
        setApproveSuccess({
          name: learnerName(learner),
          message: resetSent
            ? "The learner has been approved and a password reset link has been sent to their email so they can set their password."
            : "The learner has been approved. Use “Send reset password link” to email them a link to set their password.",
        });
        loadLearner({ silent: true });
      } else {
        globalLoader(false);
        globalAlert("error", res?.data?.message);
      }
    } catch (error) {
      globalLoader(false);
      globalAlert("error", error?.response?.data?.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>Learner profile - eC-Card</title>
        <meta name="viewport" content="width=1200, initial-scale=1" />
      </Helmet>
      <div className="admin-portal">
        <Header roleType="admin" />
        <div className={`${navbarToggle ? "active" : ""} wrapper`}>
          <SidebarComponent navbarToggle={navbarToggle} roleType="admin" menu="lms" subMenu="lms-learners" />
          <div className="main">
            <div className="main-wrapper">
              <p className="admin-banner">Learner profile</p>
              <SidebarArrow handleSidebarToggle={setNavbarToggle} />

              <div className="container-fluid pt-3 lms-learner-profile-page">
                <button type="button" className="btn lms-profile-back-btn" onClick={() => navigate("/admin/lms/learners?status=all")}>
                  <i className="fas fa-arrow-left"></i> Back to learners
                </button>

                {loading ? (
                  <div className="lms-profile-loading">
                    <i className="fas fa-circle-notch fa-spin"></i>
                    <span>Loading learner profile...</span>
                  </div>
                ) : (
                  <>
                    <section className="lms-profile-hero">
                      <div className="lms-profile-person">
                        <span className="lms-profile-avatar">{initials(learner)}</span>
                        <div>
                          <div className="lms-profile-name-row">
                            <h1>{learnerName(learner)}</h1>
                            <Badge tone={account.tone}>{account.label}</Badge>
                          </div>
                          <p>{learner?.email || "-"}</p>
                        </div>
                      </div>
                      <div className="lms-profile-summary">
                        <div style={{ backgroundColor: '#f39c12', border: 'none', padding: '15px', borderRadius: '8px', display: 'flex', flexDirection: 'column', flex: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong style={{ fontSize: '1.5rem', color: 'white', margin: 0, lineHeight: 1 }}>{enrolments.length}</strong>
                            <i className="fas fa-book" style={{ fontSize: '1.5rem', color: 'white', opacity: 0.8 }}></i>
                          </div>
                          <span style={{ fontSize: '0.75rem', color: 'white', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>Enrolled courses</span>
                        </div>
                        <div style={{ backgroundColor: '#28a745', border: 'none', padding: '15px', borderRadius: '8px', display: 'flex', flexDirection: 'column', flex: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong style={{ fontSize: '1.5rem', color: 'white', margin: 0, lineHeight: 1 }}>{completedCourses}</strong>
                            <i className="fas fa-check-circle" style={{ fontSize: '1.5rem', color: 'white', opacity: 0.8 }}></i>
                          </div>
                          <span style={{ fontSize: '0.75rem', color: 'white', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>Completed</span>
                        </div>
                        <div style={{ backgroundColor: '#007bff', border: 'none', padding: '15px', borderRadius: '8px', display: 'flex', flexDirection: 'column', flex: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong style={{ fontSize: '1.5rem', color: 'white', margin: 0, lineHeight: 1 }}>{quizAttempts.length}</strong>
                            <i className="fas fa-question-circle" style={{ fontSize: '1.5rem', color: 'white', opacity: 0.8 }}></i>
                          </div>
                          <span style={{ fontSize: '0.75rem', color: 'white', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>Quiz attempts</span>
                        </div>
                      </div>
                    </section>

                    <section className="lms-learner-detail-section">
                      <div className="lms-learner-detail-section-title">
                        <div><i className="fas fa-user"></i><h4>Learner information</h4></div>
                      </div>
                      <div className="row px-3">
                        {[
                          ["Organisation", learner?.learnerOrganisation || learner?.organisation],
                          ["Job title", learner?.jobTitle],
                          ["User type", humanize(learner?.userType)],
                          ["Region", learner?.region?.name || learner?.districtRef?.region?.name],
                          ["District", learner?.districtRef?.name || learner?.district?.name || learner?.district],
                          ["Phone", learner?.mobile || learner?.phone || learner?.phoneNumber],
                          ["Joined", formatDate(learner?.createdAt || learner?.registeredAt)],
                          ["Last active", formatDate(learner?.lastActiveAt || learner?.lastLoginAt)],
                          ["Learner ID", learnerId],
                        ].map(([label, value]) => (
                          <div key={label} className="col-md-6 mb-3">
                            <div className="d-flex align-items-center border-bottom pb-2">
                              <strong className="text-dark text-uppercase" style={{ fontSize: '0.8rem', width: '40%' }}>{label}</strong>
                              <span className="text-muted" style={{ width: '60%' }}>{value || "-"}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>

                    <section className="lms-history-card">
                      <div className="lms-history-card-title">
                        <div>
                          <h2>Course enrolment & history</h2>
                          <p>Course, module, part progress and quiz attempts.</p>
                        </div>
                        <Badge tone="muted">{historyRowCount} rows</Badge>
                      </div>
                      {historyGroups.length ? (
                        <div className="table-responsive">
                          <table className="table lms-history-table">
                            <thead>
                              <tr>
                                <th>Course / Module / Part</th>
                                <th>Status</th>
                                <th>Completed on</th>
                                <th>Quiz attempts</th>
                                <th>Best score</th>
                                <th>Pass</th>
                              </tr>
                            </thead>
                            <tbody>
                              {historyGroups.map((group) => {
                                const row = group.course;
                                const isOpen = openCourseKey === group.key;
                                return (
                                  <React.Fragment key={group.key}>
                                    <tr className={`lms-history-level-${row.level} lms-history-course ${isOpen ? "lms-history-course-open" : ""}`}>
                                      <td>
                                        <button
                                          type="button"
                                          className="lms-history-course-toggle"
                                          onClick={() => setOpenCourseKey(isOpen ? "" : group.key)}
                                          aria-expanded={isOpen}
                                        >
                                          <i className={`fas fa-chevron-${isOpen ? "down" : "right"}`}></i>
                                          <span>{row.label}</span>
                                          <small>{group.children.length} items</small>
                                        </button>
                                      </td>
                                      <td><Badge>{row.status}</Badge></td>
                                      <td>{formatDate(row.completedOn)}</td>
                                      <td>{row.attempts ?? "-"}</td>
                                      <td>{formatScore(row.bestScore)}</td>
                                      <td>{row.pass === true ? <Badge tone="success">Yes</Badge> : row.pass === false ? <Badge tone="muted">-</Badge> : "-"}</td>
                                    </tr>
                                    {isOpen && group.children.map((childRow) => (
                                      <tr key={childRow.key} className={`lms-history-level-${childRow.level} ${childRow.rowType ? `lms-history-${childRow.rowType}` : ""}`}>
                                        <td>
                                          <span className="lms-history-title" style={{ paddingLeft: `${childRow.level * 24}px` }}>
                                            <i className="fas fa-level-up-alt"></i>
                                            {childRow.label}
                                          </span>
                                        </td>
                                        <td><Badge>{childRow.status}</Badge></td>
                                        <td>{formatDate(childRow.completedOn)}</td>
                                        <td>{childRow.attempts ?? "-"}</td>
                                        <td>{formatScore(childRow.bestScore)}</td>
                                        <td>{childRow.pass === true ? <Badge tone="success">Yes</Badge> : childRow.pass === false ? <Badge tone="muted">-</Badge> : "-"}</td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="lms-learner-course-empty">
                          <i className="fas fa-book"></i>
                          <div><strong>No course history</strong><span>This learner has not started any course yet.</span></div>
                        </div>
                      )}
                    </section>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {confirmApprove && (
        <ConfirmationModal
          open={confirmApprove}
          deleteInfo={[`Learner : ${learnerName(learner)}`]}
          description="Are you sure you want to approve this learner?"
          onCloseModal={approveLearner}
        />
      )}
      {approveSuccess && (
        <div
          className="modal fade show lms-approve-success-modal"
          style={{ display: "block" }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="lms-approve-success-title"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content lms-approve-success-content">
              <span className="lms-approve-success-icon">
                <i className="fas fa-check-circle"></i>
              </span>
              <h3 id="lms-approve-success-title" className="lms-approve-success-title">
                Learner approved
              </h3>
              <p className="lms-approve-success-name">{approveSuccess.name}</p>
              <p className="lms-approve-success-text">{approveSuccess.message}</p>
              <button
                type="button"
                className="btn primary-btn lms-approve-success-btn"
                onClick={() => setApproveSuccess(null)}
                autoFocus
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LmsLearnerDetail;
